import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSessionStore } from '@/store/session-store';

/** Why the session ended. Drives the notice the login screen shows on arrival. */
export const SIGN_OUT_REASON_TIMEOUT = 'timeout';

export type SignOutReason = typeof SIGN_OUT_REASON_TIMEOUT;

/**
 * Ends the provider's session (US-003 AC-001/AC-002).
 *
 * The TanStack Query cache is cleared as well as the store: policy and claims responses are held in
 * memory by the cache, and leaving them there would let the next person on the same tab see the previous
 * provider's data. The redirect replaces the history entry so the back button cannot return to an
 * authenticated screen (AC-005).
 *
 * Where the provider was is deliberately NOT recorded. A portal URL can carry a policy number and a date
 * of birth, and router state is written to disk with the session-restore data — so replaying it for
 * whoever signs in next on a shared workstation would hand them the previous provider's identifiers.
 * The deep link is worth less than that (US-003 AC-004).
 */
export function useSessionSignOut(): (reason?: SignOutReason) => void {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const signOut = useSessionStore((state) => state.signOut);

  return useCallback(
    (reason?: SignOutReason) => {
      signOut();
      queryClient.clear();
      navigate('/login', { replace: true, state: reason === undefined ? null : { reason } });
    },
    [navigate, queryClient, signOut],
  );
}
